import { Toast } from "primereact/toast";
import { useRef, useState, useMemo, useEffect } from "react";
import ConfirmDocument from "../../components/ConfirmDocument/ConfirmDocument";
import PopupImage from "../../components/PopupImage/PopupImage";
import SEO from "../../components/SEO/SEO";
import AdvancedFilterModal from "../../components/SearchProducts/Components/AdvancedFilterModal/AdvancedFilterModal";
import Wrapper from "../../components/Wrapper/Wrapper";
import { useToastContext } from "../../contexts/ToastContext/ToastContext";
import useCategories from "../../hooks/useCategories";
import useGetStoresNames from "../../hooks/useGetStoresNames";
import useSearchProducts from "../../hooks/useSearchProducts";
import { useToggle } from "../../hooks/useToggle";
import localeText from "../../locale";
import { cloudinaryInstance } from "../../utilities/Cloudinary/cloudinaryInstance";
import { locale } from "../../utilities/locale/locale";
import "./Shop.css";
import "./../css/layout-blue.css";
import BreadCrumbShop from "./components/BreadCrumbShop/BreadCrumbShop";
import Products from "./components/Products/Products";
import SectionCategories from "./components/SectionCategories/SectionCategories";
import ShopSections from "./components/ShopSections/ShopSections";
import SubcategoriesFilter from "./components/SubcategoriesFilter/SubcategoriesFilter";
import useShop from "./hooks/useShop";

import { useLocation } from "react-router-dom";
import CustomerBanner from "./components/CustomerBanner/CustomerBanner";
import PrimaryBanner from "./components/PrimaryBanner/PrimaryBanner";
import LandingFooter from "../Landing/components/LandingFooter/LandingFooter";
import LandingCopyright from "../Landing/components/LandingCopyright/LandingCopyright";
import SecondaryBanner from "./components/SecondaryBanner/SecondaryBanner";

import BreadCrumbSearch from "../ProductInfo/components/BreadCrumbSearch/BreadCrumbSearch";
import MapLeaflet from "../../components/Map";

const Shop = ({ search: searchQueryParam }) => {
  const [visible, setVisible] = useState(false);
  const [popupImage, setPopupImage] = useState("");
  const { value, setAsFalse, setAsTrue } = useToggle();
  const {
    value: isVisibleFilterModal,
    setAsFalse: setAsFalseFilterModal,
    setAsTrue: setAsTrueFilterModal,
  } = useToggle();

  const url = useLocation();
  const isFringeBenefitsPage = url?.pathname?.toLowerCase()?.includes("fringe");

  function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
  }

  let query = useQuery();

  const { showErrorToast } = useToastContext();
  const { storeNames, isLoading: isLoadingStoreNames } = useGetStoresNames({
    onError: (code) => showErrorToast({ code }),
  });

  const toast = useRef(null);

  const {
    loadingProducts,
    setLoadingProducts,
    products,
    loadingSearch,
    search,
    setProducts,
    searchMessage,
    setSearchMessage,
    searchParams,
    setSearchParams,
    advancedSearchParams,
    setAdvancedSearchParams,
    totalPages,
    totalProducts,
    fringeBenefitsSubCategories,
  } = useSearchProducts({
    searchTerm: searchQueryParam,
  });

  const { categories, loadingCategories } = useCategories({
    onCategoriesError: (code) => showErrorToast({ code }),
  });

  const {
    selectedCategory,
    subcategories,
    selectedSubcategory,
    showPopupImage,
    urlRedirect,
    sectionsProducts,
    setShowPopupImage,
    setSelectedSubcategory,
    setSubcategories,
    setSelectedCategory,
  } = useShop({
    searchTerm: searchQueryParam,
    loadingProducts,
    setLoadingProducts,
    onLoadPopupImage: setPopupImage,
    setProducts,
    onSubCategoriesError: (code) => showErrorToast({ code }),
    onCategoriesError: (code) => showErrorToast({ code }),
  });

  const handleSelectedCategory = (category) => {
    query.delete("category");
    setSearchMessage("");
    setSubcategories(null);
    setSelectedSubcategory(null);
    setSelectedCategory(category);
    setSubcategories(category.subCategories);
  };

  const handleMobileSelectedCategory = (category) => {
    query.delete("category");
    const descriptions = {
      en: category.descriptionEnglish,
      it: category.descriptionItalian,
      pt: category.descriptionPortuguese,
      es: category.descriptionSpanish,
    };
    const description = descriptions[locale]?.toLowerCase() ?? "";
    setSearchMessage("");
    setSubcategories(null);
    setSelectedSubcategory(null);
    setSelectedCategory({ ...category, descriptionCurrentLocale: description });
    setSubcategories(category.subCategories);
  };

  const handleResetMobileCategories = () => {
    setSelectedSubcategory(null);
    setSelectedCategory(null);
    setSubcategories([]);
    setProducts([]);
  };

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === "/") {
      setSearchMessage(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (query.get("category") && categories.length > 0) {
      const queryCategoryId = query.get("category");
      const currentCategory = categories.find(
        (category) => category.id === Number(queryCategoryId)
      );
      /* handleMobileSelectedCategory(currentCategory); */
    }

    if (query.get("subcategory") && subcategories?.length > 0) {
      const querySubcategoryId = query.get("subcategory");
      const currentSubcategory = subcategories.find(
        (subcategory) => subcategory.id === Number(querySubcategoryId)
      );
      setSelectedSubcategory(currentSubcategory);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length, subcategories.length]);

  return (
    <>
      <SEO
        title="Extraconomy"
        image="https://extraconomy.com/static/media/logo-color-horizontal.05fe6ab611037d6aaf49.png"
        description="Una piattaforma innovativa on-line di economia cooperativa e circolare."
      />
      {showPopupImage && (
        <PopupImage
          urlRedirect={urlRedirect}
          image={popupImage}
          onClick={() => setShowPopupImage(false)}
        />
      )}
      <Toast ref={toast} />
      <ConfirmDocument />

      <AdvancedFilterModal
        setNotVisible={setAsFalseFilterModal}
        visible={isVisibleFilterModal}
        searchParams={advancedSearchParams}
        setSearchParams={setAdvancedSearchParams}
        isLoading={loadingSearch}
        isLoadingStoreNames={isLoadingStoreNames}
        storeNames={storeNames}
        isAdvancedFilter={true}
        filter={() => search({ expression: "ALL", onError: (code) => showErrorToast({ code }) })}
      />
      <div className="content-grid bg-white">
        {!searchQueryParam && !searchMessage && !selectedCategory && (
          <>
            <PrimaryBanner />

            <SectionCategories
              categories={categories}
              handleSelectedCategory={handleSelectedCategory}
              selectedCategory={selectedCategory}
              loadingCategories={loadingCategories}
            />
          </>
        )}

        <Wrapper>
          <div className="mb-8">
            <div className="my-5">
              {selectedCategory && (
                <>
                  <BreadCrumbShop
                    selectedCategory={selectedCategory}
                    selectedSubcategory={selectedSubcategory ? selectedSubcategory : null}
                    setProducts={setProducts}
                    setSelectedCategory={setSelectedCategory}
                    setSelectedSubcategory={setSelectedSubcategory}
                  />
                  {/* <img src={bannerSample} alt="Banner" className="w-full mb-6" /> */}
                </>
              )}

              {searchParams.category && searchParams.category !== "ALL" && searchQueryParam && (
                <BreadCrumbSearch categoryId={searchParams.category} />
              )}

              {isFringeBenefitsPage && (
                <div className="mb-5">
                  <h1 className="text-4xl font-bold">Fringe Benefit Store</h1>
                </div>
              )}

              <div className="flex align-items-start sm:justify-content-start justify-content-center">
                {selectedCategory && (
                  <SubcategoriesFilter
                    subcategories={subcategories}
                    selectedSubcategory={selectedSubcategory}
                    onSelectedSubcategory={setSelectedSubcategory}
                  />
                )}

                <Products
                  setProducts={setProducts}
                  isLoading={loadingProducts}
                  products={products}
                  searchQueryParam={searchQueryParam}
                  searchMessage={searchMessage}
                  isSelectedCategory={!!selectedCategory}
                  searchParams={searchParams}
                  setSearchParams={setSearchParams}
                  visible={value}
                  onHide={setAsFalse}
                  isLoadingStoreNames={isLoadingStoreNames}
                  storeNames={storeNames}
                  totalPages={totalPages}
                  totalProducts={totalProducts}
                  fringeBenefitsSubCategories={fringeBenefitsSubCategories}
                />
              </div>
            </div>
            {/* <CustomerBanner /> */}
            {!searchQueryParam && !searchMessage && !selectedCategory && (
              <>
                <ShopSections toast={toast} sectionsProducts={sectionsProducts} />
                {/*  <SecondaryBanner /> */}
              </>
            )}
          </div>
        </Wrapper>
        {/* <Footer /> */}
        <MapLeaflet />
        <LandingFooter />
        <LandingCopyright />
        <section
          className="md:py-5 full-width"
          style={{ backgroundColor: "#FFFFFF", color: "#7A869A", borderTop: "1px solid #E0E7FF" }}
        >
          <div className="grid gap-3 m-0">
            <div className="col-12 md:col text-center md:text-left">
              <div className="text-sm font-regular">EXTRACONOMY GLOBAL srl</div>
              <div className="text-sm font-regular">Via Nomentana, 1018 - 00137 Roma (IT)</div>
            </div>
            <div className="col-12 md:col text-center md:text-left">
              <div className="text-sm font-regular">P. Iva e C.F. 16948681008 | REA 1686351</div>
            </div>
            <div className="col-12 md:col text-center md:text-right">
              <div className="text-sm font-regular">Privacy Policy</div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Shop;
